import { computed, ref } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import fetchData from '@/util/fetch'

// The breaker of a Solarminds Cast4all meter, through the tickets endpoints the meter activation page uses
// (tickets backend/api/tickets.py, services/c4a.py breaker_status):
//   GET  /tickets/api/v1/eleena/solarminds/breaker-status  { has_breaker, breaker_status: On | Off | Unknown,
//        source, source_time, pending: On | Off | null, last_switch: { status, result_message, ... } }
//   POST /tickets/api/v1/eleena/solarminds/breaker  { serial_number, on }
// Both need role admin/tickets and action Eleena:register_meters. Cast4all takes a while to carry out a
// switch: after a switch made here the status is re-read every few seconds until it shows the new position
// and nothing is pending, for at most a minute. Not only while pending: Cast4all can register the switch a
// moment after the POST returns, so the first read may not show it yet.
const FOLLOW_MS = 5000
const FOLLOW_FOR_MS = 60 * 1000

// fetchData throws the API's JSON body: { message } from the endpoint, { detail } from the auth check or
// FastAPI itself ('Not Found' when the tickets backend lacks the route). The endpoint's own messages are
// Dutch sentences; anything else gets the fallback in front so the reader knows what failed.
const errorText = (e, fallback) => {
  if (!e) return null
  if (e.status === 'error' && e.message) return e.message
  const detail = typeof e.detail === 'string' ? e.detail : e.detail?.message
  const reason = detail || e.message
  return reason ? `${fallback} (${reason})` : fallback
}

export function useC4aBreaker(serialNumber, enabled) {
  const queryClient = useQueryClient()
  const queryKey = computed(() => ['c4aBreaker', serialNumber.value])
  // per serial: { until, status } of the last switch made here, so another meter's switch is not followed
  const following = ref({})

  const { data: info, error, isFetching } = useQuery({
    queryKey,
    queryFn: () => fetchData(
      `/tickets/api/v1/eleena/solarminds/breaker-status?serial_number=${encodeURIComponent(serialNumber.value)}`
    ),
    enabled: computed(() => !!serialNumber.value && enabled.value),
    refetchOnWindowFocus: false,
    retry: false,
    refetchInterval: (data) => {
      const follow = following.value[serialNumber.value]
      if (!follow || Date.now() > follow.until) return false
      return data?.pending || data?.breaker_status !== follow.status ? FOLLOW_MS : false
    },
  })

  const isSwitching = ref(false)
  const switchError = ref(null)
  const setBreaker = async (on) => {
    isSwitching.value = true
    switchError.value = null
    const serial = serialNumber.value
    try {
      await fetchData('/tickets/api/v1/eleena/solarminds/breaker', {
        method: 'POST',
        body: JSON.stringify({ serial_number: serial, on }),
      })
      following.value = { ...following.value, [serial]: { until: Date.now() + FOLLOW_FOR_MS, status: on ? 'On' : 'Off' } }
      await queryClient.invalidateQueries(['c4aBreaker', serial])
    } catch (e) {
      switchError.value = errorText(e, 'Schakelen bij Cast4all mislukt')
    } finally {
      isSwitching.value = false
    }
  }

  const errorMessage = computed(() => errorText(error.value, 'Breakerstatus ophalen bij Cast4all mislukt'))

  return { info, errorMessage, isFetching, isSwitching, switchError, setBreaker }
}
